"use client";
import React, {Suspense, useEffect, useState} from "react";
import Sidebar, {Notification} from "../Sidebar";
import Header from "../Header";
import {toast, Toaster} from "react-hot-toast";
import {createClient} from "@repo/shared/supabase/client";
import UserContext from "./UserContext";
import NotificationContext from "./NotificationContext";
import sidebarItems from "@/SidebarItems";
import Image from "next/image";
import {useReportWebVitals} from "next/web-vitals";
import SidebarTutorial from "../SidebarTutorial";

const CURRENT_VERSION = process.env.NEXT_PUBLIC_APP_VERSION || "dev";
console.log("CURRENT_VERSION", CURRENT_VERSION)

export function Spinner() {
    return <div
        className="flex h-screen items-center justify-center bg-white dark:bg-black">
        <div
            className="h-16 w-16 animate-spin rounded-full border-4 border-solid border-primary border-t-transparent"></div>
    </div>
}

export default function DefaultLayoutClient({
                                                children,
                                                company,
                                                user,
                                                user_internal,
                                                initial_notifications,
                                                initial_permissions
                                            }: {
    children: React.ReactNode;
    company: any,
    user: any,
    user_internal: any,
    initial_notifications: any,
    initial_permissions: any
}) {
    if (process.env.NODE_ENV === 'production') {
        useReportWebVitals((metric) => {
            console.log(metric)
            //TODO send vitals to supabase for metrics
        })
    }

    const supabase = createClient();
    const [permissions, setPermissions] = useState<string[]>(initial_permissions ?? user_internal?.permissions);
    const [notifications, setNotifications] = useState<Notification[]>(initial_notifications ?? []);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [shouldReload, setShouldReload] = useState(false);

    useEffect(() => {
        const checkVersion = async () => {
            try {
                const res = await fetch("/api/version");
                const data = await res.json();
                if (data.version !== CURRENT_VERSION) {
                    setShouldReload(true);
                }
            } catch (error) {
                console.error("Version check failed", error);
            }
        };
        if(process.env.NODE_ENV === 'production') {
            const interval = setInterval(checkVersion, 10 * 60 * 1000); // Check every 60 seconds
            return () => clearInterval(interval);
        }
    }, []);

    useEffect(() => {
        if (shouldReload) {
            window.location.reload();
        }
    }, [shouldReload]);

    const subscribeToNotifications = () => {
        const channel = supabase
            .channel("schema-db-changes")
            .on(
                "postgres_changes",
                {
                    event: "INSERT",
                    schema: "public",
                    table: "notifications",
                    filter: `tenant_id=eq.${company?.tenant_id}`,
                },
                (payload) => {
                    const newNotification = payload.new as Notification;
                    toast.success(newNotification.payload.message);
                    setNotifications((prev) => [newNotification, ...prev]);
                }
            )
            .on(
                "postgres_changes",
                {
                    event: "UPDATE",
                    schema: "public",
                    table: "notifications",
                    filter: `tenant_id=eq.${company?.tenant_id}`,
                },
                (payload) => {
                    const updatedNotification = payload.new as Notification;
                    setNotifications((prev) =>
                        prev.map((notif) =>
                            notif.id === updatedNotification.id ? updatedNotification : notif
                        )
                    );
                }
            )
            .subscribe((status) => {
                // toast.success(`Realtime subscription status: ${status}`);
                if (status === "CLOSED" || status === "TIMED_OUT") {
                    if (reconnectAttempts === 0) {
                        handleReconnection(channel);
                    }
                }
            });

        return channel;
    };

    const handleReconnection = (channel: ReturnType<typeof supabase.channel>) => {
        setReconnectAttempts((prev) => prev + 1);

        // Unsubscribe and try to reconnect
        setTimeout(() => {
            if (reconnectAttempts < 5) {
                // subscribeToNotifications();
            } else {
                toast.error("Failed to reconnect after multiple attempts.");
            }
        }, 3000); // Retry every 3 seconds
    };

    useEffect(() => {
        if (!company?.tenant_id) return;
        const channel = subscribeToNotifications();

        return () => {
            supabase.removeChannel(channel);
        };
    }, [company?.tenant_id, supabase]);

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [sidebarTutorialOpen, setSidebarTutorialOpen] = useState(false);
    const toggleSideBarOpen = () => {
        setSidebarTutorialOpen(!sidebarTutorialOpen)
    }

    return (
        <>
            <UserContext.Provider value={{user, user_internal}}>
                <NotificationContext.Provider value={{notifications}}>
                    {/* <!-- ===== Page Wrapper Start ===== --> */}
                    <div className="flex h-screen overflow-hidden">
                        {/* <!-- ===== Sidebar Start ===== --> */}
                            <Sidebar sidebarItems={sidebarItems} sidebarOpen={sidebarOpen}
                                     setSidebarOpen={setSidebarOpen}
                                     permissions={permissions} image={<Image
                                className="dark:block"
                                src={company?.logo ?? "/images/Original Logo.png"}
                                alt="Logo"
                                width={0}
                                height={0}
                                sizes="100vw"
                                priority={true}
                                style={{
                                    width: '100%',
                                    maxWidth: '120px', // imposta la larghezza massima desiderata
                                    height: 'auto'
                                }}
                            />}/>
                        {/* <!-- ===== Sidebar End ===== --> */}

                        {/* <!-- ===== Content Area Start ===== --> */}
                        <div className="relative flex flex-1 flex-col overflow-y-auto overflow-x-hidden">
                            {/* <!-- ===== Header Start ===== --> */}
                                <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} image={company?.logo}
                                        setSidebarTutorialOpen={toggleSideBarOpen} user={user} calendar_enabled={false}/>
                            {/*{process.env.NODE_ENV === 'production' ?*/}
                                <SidebarTutorial sidebarOpen={sidebarTutorialOpen} setSidebarOpen={setSidebarTutorialOpen}/>
                                {/*: ""}*/}
                            {/* <!-- ===== Header End ===== --> */}
                            <Toaster position="bottom-right"   containerStyle={{zIndex: 99999999999999}} toastOptions={{duration: 10000}}/>
                            {/* <!-- ===== Main Content Start ===== --> */}
                            <main>
                                <div className="h-full mx-auto max-w-screen-2xl p-4 md:p-6 2xl:p-10">
                                            {children}
                                </div>
                            </main>
                            {/* <!-- ===== Main Content End ===== --> */}
                        </div>
                        {/* <!-- ===== Content Area End ===== --> */}
                    </div>
                    {/* <!-- ===== Page Wrapper End ===== --> */}
                </NotificationContext.Provider>
            </UserContext.Provider>
        </>
    );
}
